<template lang="pug">
.d-flex.no-wrap.pa-4#agent-cards-section(
    @mouseenter="stopAgentScroll(true)"
    @mouseleave="resumeAgentScroll"
    ref="agentCarousel"
    :style="carouselStyle"
)
    template(v-if="validAgents")
        AgentStatusCard(
            v-for="agent in scopedAgents"
            :key="'original-' + agent.account_user_id"
            :agent-name="agent.user_full_name"
            :agent-status-type="agent.agent_status_type"
            :agent-status-name="agent.agent_status_name"
            :agent-status-warning-rule="agentStatusWarningRule"
            :agent-status-critical-rule="agentStatusCriticalRule"
            :outbound-calls-today="agent.outbound_calls_today"
            :inbound-calls-today="agent.inbound_calls_today"
            :is-online="agent.is_online"
            :time-in-seconds="agent.time_in_seconds"
            :status="agent.status"
            :table-row-count="dashboardSummaryObjects?.length || 13"

        )
    template(v-if="scopedAgents.length > 6")
        AgentStatusCard(
            v-for="agent in scopedAgents"
            :key="'clone-' + agent.account_user_id"
            :agent-name="agent.user_full_name"
            :agent-status-type="agent.agent_status_type"
            :agent-status-name="agent.agent_status_name"
            :agent-status-warning-rule="agentStatusWarningRule"
            :agent-status-critical-rule="agentStatusCriticalRule"
            :outbound-calls-today="agent.outbound_calls_today"
            :inbound-calls-today="agent.inbound_calls_today"
            :is-online="agent.is_online"
            :time-in-seconds="agent.time_in_seconds"
            :status="agent.status"
            :table-row-count="dashboardSummaryObjects?.length || 13"
        )
    template(v-if="!validAgents && !agentsLoading")
        v-alert.text-center.custom-alert(outlined type='warning' dense border='left' :style="{margin: '16px', width: '100%'}")
            | There are currently no agents online for this account.
    template(v-if="agentsLoading")
        v-progress-linear.white--text( indeterminate :style="{width: '100%'}")
            | Loading agents...
</template>

<script lang="ts">
import Vue from 'vue';

// Components
import AgentStatusCard from './agent_status_card.vue';

export default Vue.extend({
    data() {
        return {
            agentScrollSpeed: 1,
            agentScrollInterval: null as any,
            isAgentScrollPaused: false,
            lastAgentCarouselSignature: '',
            lastProgrammaticScrollAt: 0,
            scrollPosition: 0,
        }
    },
    methods: {
        clearAgentScrollInterval() {
            if (this.agentScrollInterval) {
                clearInterval(this.agentScrollInterval);
                this.agentScrollInterval = null;
            }
        },
        startAgentScroll(retryCount = 0) {
            if (!this.shouldAgentsScroll || this.agentScrollInterval || this.isAgentScrollPaused) {
                return;
            }
            
            this.$nextTick(() => {
                const carousel = this.$refs.agentCarousel as HTMLElement;
                if (!carousel) return;
                
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        const element = this.$refs.agentCarousel as HTMLElement;
                        if (!element || !this.shouldAgentsScroll || this.agentScrollInterval || this.isAgentScrollPaused) {
                            return;
                        }
                        
                        element.scrollLeft = this.scrollPosition;
                        
                        const maxScroll = element.scrollWidth - element.clientWidth;
                        if (maxScroll <= 0) {
                            if (retryCount < 20) {
                                this.startAgentScroll(retryCount + 1);
                            }
                            return;
                        }
                        
                        const originalScrollWidth = element.scrollWidth / 2;
                        this.agentScrollInterval = setInterval(() => {
                            const scrollSpeed = this.agentScrollSpeed * Number(this.cookieSettingsObject?.agentScrollSpeedMultiplier || 1);
                            if (scrollSpeed <= 0) return;

                            const el = this.$refs.agentCarousel as HTMLElement;
                            if (!el || this.isAgentScrollPaused) return;
                            
                            this.lastProgrammaticScrollAt = Date.now();
                            
                            if (this.scrollPosition >= originalScrollWidth - 1) {
                                this.scrollPosition = 0;
                                el.scrollLeft = 0;
                            } else {
                                this.scrollPosition += scrollSpeed;
                                el.scrollLeft = this.scrollPosition;
                            }
                        }, 25);
                    }, 150);
                });
            });
        },
        stopAgentScroll(isHover: boolean = false) {
            if (isHover && !this.cookieSettingsObject.pauseAgentScrollOnHover) return;
            
            this.clearAgentScrollInterval();
            this.isAgentScrollPaused = true;
        },
        resumeAgentScroll() {
            this.isAgentScrollPaused = false;
            this.startAgentScroll();
        },
        preventManualScroll(event: Event) {
            // Prevent all manual scrolling - only allow programmatic scrolling
            event.preventDefault();
            event.stopPropagation();
            return false;
        },
        getAgentCarouselSignature(agents: any[]): string {
            return (agents || [])
                .map(agent => agent?.account_user_id)
                .filter(id => id > 0)
                .join('|');
        },
        handleScopedAgentsChange(newVal: any[], oldVal: any[]) {
            const newSig = this.getAgentCarouselSignature(newVal);
            const hadAgents = (oldVal?.length || 0) > 0;
            const hasAgents = (newVal?.length || 0) > 0;
            const shouldScroll = (newVal?.length || 0) > 6;

            if (!hadAgents && hasAgents) {
                this.resetAgentScroll(newVal, shouldScroll);
                return;
            }

            if (hadAgents && !hasAgents) {
                this.clearAgentScrollInterval();
                this.scrollPosition = 0;
                this.lastAgentCarouselSignature = '';
                return;
            }

            // Same agent membership — status/stat updates should not interrupt scroll
            if (newSig === this.lastAgentCarouselSignature) {
                if (shouldScroll && !this.agentScrollInterval && !this.isAgentScrollPaused) {
                    this.startAgentScroll();
                }
                return;
            }

            // New agents added or removed — keep current scroll position
            this.lastAgentCarouselSignature = newSig;

            if (shouldScroll) {
                this.isAgentScrollPaused = false;
                if (!this.agentScrollInterval) {
                    this.startAgentScroll();
                }
                return;
            }

            this.clearAgentScrollInterval();
            this.scrollPosition = 0;
        },
        resetAgentScroll(newVal: any[], shouldScroll: boolean) {
            this.lastAgentCarouselSignature = this.getAgentCarouselSignature(newVal);

            this.clearAgentScrollInterval();
            this.isAgentScrollPaused = false;
            this.scrollPosition = 0;

            this.$nextTick(() => {
                const carousel = this.$refs.agentCarousel as HTMLElement;
                if (carousel) {
                    carousel.scrollLeft = 0;
                }

                if (newVal?.length > 0 && shouldScroll) {
                    this.startAgentScroll();
                }
            });
        },
    },
    computed: {
        agentStatusWarningRule() {
            const rule = this.rules?.find(rule => rule.rule_action === 'agent_status_warning_account');
            return {
                metric: rule?.metric || 120, 
                display_color: rule?.display_color || 'var(--orange)',
            };
        },
        agentStatusCriticalRule() {
            const rule = this.rules?.find(rule => rule.rule_action === 'agent_status_critical_account');
            return {
                metric: rule?.metric || 18000, 
                display_color: rule?.display_color || 'var(--error)',
            };
        },
        carouselStyle() {
            if (this.scopedAgents?.length <= 6) {
                return {
                    'overflow-x': 'auto',
                    'scroll-behavior': 'smooth',
                    'justify-content': 'center'
                };
            } else {
                return {
                    'overflow-x': 'auto',
                    'scroll-behavior': 'auto'  // Use 'auto' for programmatic scrolling
                };  
            }
        },
        scopedAgents() {
            if (!(this.agents?.length > 0)) {
                return [];
            }
            
            if (this.cookieSettingsObject.hideUnscopedAgentsInCardSection && this.scopeParameters.scopeId > 0) {
                switch (this.scopeParameters.scopeType) {
                    case 'team_organization':
                        const teams = dataBank.r_teams;
                        if (!teams || teams.length === 0) {
                            return [];
                        }

                        const team = teams.find(team => team.team_id === this.scopeParameters.scopeId);
                        if (!team) {
                            return [];
                        }

                        return this.agents?.filter(agent => team.user_ids?.includes(agent?.user_id) || false);
                    default:
                        return this.agents;
                }
            }

            return this.agents;
        },
        shouldAgentsScroll() {
            return this.scopedAgents?.length > 6;
        },
        validAgents() {
            return this.scopedAgents?.length > 0;
        },
    },
    components: {
        AgentStatusCard,
    },
    props: {
        agents: {
            type: Array,
            required: true,
        },
        agentsLoading: {
            type: Boolean,
            required: true,
        },
        cookieSettingsObject: {
            type: Object,
            required: true,
        },
        dashboardSummaryObjects: {
            type: Array,
            required: false,
        },
        rules: {
            type: Array,
            required: false,
        },
        scopeParameters: {
            type: Object,
            required: false,
        },
    },
    watch: {
        scopedAgents: {
            immediate: true,
            handler(newVal, oldVal) {
                this.handleScopedAgentsChange(newVal, oldVal);
            },
        },
    },
    created() {
    },
    destroyed() {
        this.stopAgentScroll();
        
        // Clean up event listeners
        this.$nextTick(() => {
            const carousel = this.$refs.agentCarousel as HTMLElement;
            if (carousel) {
                carousel.removeEventListener('wheel', this.preventManualScroll);
                carousel.removeEventListener('touchmove', this.preventManualScroll);
            }
        });
    },
    mounted() {
        this.$nextTick(() => {
            const carousel = this.$refs.agentCarousel as HTMLElement;
            if (carousel) {
                // Prevent manual scrolling via wheel and touch
                carousel.addEventListener('wheel', this.preventManualScroll, { passive: false });
                carousel.addEventListener('touchmove', this.preventManualScroll, { passive: false });
                
                // Prevent drag scrolling by preventing default on mousedown
                carousel.addEventListener('mousedown', (e) => {
                    if (e.button === 0) { // Left mouse button
                        e.preventDefault();
                    }
                }, { passive: false });
            }
            
            this.startAgentScroll();
        });
    },
})
</script>

<style lang="scss" scoped>

    #agent-cards-section {
        min-height: 9vh !important;
        flex-shrink: 0;  /* Prevent cards section from shrinking */
        gap: 8px;
        -ms-overflow-style: none;  /* Hide scrollbar IE and Edge */
        scrollbar-width: none;  /* Hide scrollbar Firefox */
        overscroll-behavior: none;  /* Prevent scroll chaining and bounce effects */
        touch-action: pan-y pinch-zoom;  /* Allow vertical scrolling and pinch zoom, prevent horizontal panning */
        
        &::-webkit-scrollbar {
            display: none;  /* Hide scrollbar Chrome, Safari and Opera */
        }
    }

    .custom-alert {
        ::v-deep .v-alert__icon {
            font-size: inherit !important;
            height: auto !important;
            min-width: auto !important;
            width: auto !important;
        }
    }

</style>